import { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { Maximize2, Minimize2, RotateCcw, ExternalLink } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { VRTour } from '@/features/properties/hooks/useVRTours';

interface VRTourViewerProps {
  tour: VRTour;
  onViewCountIncrement?: (tourId: string) => void;
  className?: string;
  autoPlay?: boolean;
}

export function VRTourViewer({ tour, onViewCountIncrement, className, autoPlay = true }: VRTourViewerProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hasIncrementedView, setHasIncrementedView] = useState(false);

  // Auto-increment view count on mount when autoPlay is true
  useEffect(() => {
    if (autoPlay && !hasIncrementedView) {
      setHasIncrementedView(true);
      onViewCountIncrement?.(tour.id);
    }
  }, [autoPlay, hasIncrementedView, onViewCountIncrement, tour.id]);

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const toggleFullscreen = () => {
    if (containerRef.current) {
      if (!isFullscreen) {
        containerRef.current.requestFullscreen?.();
      } else {
        document.exitFullscreen?.();
      }
    }
  };

  const resetTour = () => {
    if (iframeRef.current) {
      // Re-setting src forces the cross-origin iframe to reload
      const { src } = iframeRef.current;
      iframeRef.current.src = src;
    }
  };

  const openInNewTab = () => {
    window.open(tour.link_url, '_blank');
  };

  // Create Matterport embed URL with parameters for instant playback
  // play=1: auto-start, qs=1: quickstart, help=0: hide help, brand=0: hide branding
  // Do not add mt=0 or mls — they hide Mattertags / strip tag content
  const buildEmbedUrl = (url: string) => {
    if (!url.includes('matterport.com')) return url;

    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}play=1&qs=1&help=0&brand=0`;
  };

  const embedUrl = buildEmbedUrl(tour.link_url);

  return (
    <Card className={cn("overflow-hidden flex flex-col h-full", className)}>
      <CardHeader className="pb-3 shrink-0">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <CardTitle className="text-xl">{tour.link_name || 'Matterport Tour'}</CardTitle>
            {tour.link_metadata?.tour_description && (
              <CardDescription>{tour.link_metadata.tour_description}</CardDescription>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">
              {tour.link_metadata?.view_count || 0} {tour.link_metadata?.view_count === 1 ? 'view' : 'views'}
            </Badge>
            <Badge variant="default" className="bg-green-600">
              Live
            </Badge>
          </div>
        </div>

        <div className="flex items-center gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={resetTour}
            className="gap-2"
          >
            <RotateCcw className="h-4 w-4" />
            Reset
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={toggleFullscreen}
            className="gap-2"
          >
            {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={openInNewTab}
            className="gap-2"
          >
            <ExternalLink className="h-4 w-4" />
            Open in New Tab
          </Button>
        </div>
      </CardHeader>

      <CardContent className="p-0 flex-1 flex flex-col">
        <div 
          ref={containerRef}
          className="relative bg-muted flex-1 min-h-0"
        >
          {/* Matterport iframe - plays instantly without overlay */}
          <iframe
            ref={iframeRef}
            src={embedUrl}
            className="w-full h-full border-0"
            title={tour.link_name || 'Matterport Tour'}
            allowFullScreen
            allow="vr; xr; accelerometer; gyroscope; fullscreen"
          />
        </div>

        {/* Tour metadata */}
        {tour.link_metadata && Object.keys(tour.link_metadata).filter(k => !['view_count', 'tour_description', 'thumbnail_url'].includes(k)).length > 0 && (
          <div className="p-4 border-t bg-muted/50 shrink-0">
            <h4 className="text-sm font-medium mb-2">Tour Information</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              {Object.entries(tour.link_metadata)
                .filter(([key]) => !['view_count', 'tour_description', 'thumbnail_url'].includes(key))
                .map(([key, value]) => (
                  <div key={key}>
                    <span className="text-muted-foreground capitalize">
                      {key.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim()}:
                    </span>
                    <span className="ml-2">{String(value)}</span>
                  </div>
                ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
